import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import StatsCard from '../../pages/user-dashboard/components/StatsCard';
import MetricsCard from './components/MetricsCard';
import UserManagementTable from './components/UserManagementTable';
import JobOversightTable from './components/JobOversightTable';
import ReviewOversightTable from './components/ReviewOversightTable';
import DisputeResolutionPanel from './components/DisputeResolutionPanel';
import AnalyticsChart from './components/AnalyticsChart';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0,
    activeJobs: 0,
    completedTransactions: 0,
    totalRevenue: 0,
    newUsersToday: 0,
    pendingReviews: 0,
    activeDisputes: 0,
    flaggedContent: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [flaggedJobs, setFlaggedJobs] = useState([]);
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);

  useEffect(() => {
    fetchAdminData();
  }, []);

  // Admin authentication guard
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAdmin, navigate]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch platform statistics
      const [usersResult, jobsResult, transactionsResult, earningsResult, ratingsResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, created_at, is_active', { count: 'exact' }),
        supabase?.from('jobs')?.select('id, status', { count: 'exact' }),
        supabase?.from('transactions')?.select('id, amount, status', { count: 'exact' }),
        supabase?.from('earnings')?.select('amount'),
        supabase?.from('ratings')?.select('id, rating, created_at')
      ]);

      // Calculate stats
      const totalUsers = usersResult?.count || 0;
      const activeJobs = jobsResult?.data?.filter(j => j?.status === 'active')?.length || 0;
      const completedTransactions = transactionsResult?.data?.filter(t => t?.status === 'completed')?.length || 0;
      const totalRevenue = earningsResult?.data?.reduce((sum, e) => sum + parseFloat(e?.amount || 0), 0) || 0;
      
      // Calculate new users today
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const newUsersToday = usersResult?.data?.filter(u => {
        const createdDate = new Date(u?.created_at);
        return createdDate >= today;
      })?.length || 0;

      const pendingReviews = ratingsResult?.data?.filter(r => {
        const reviewDate = new Date(r?.created_at);
        const weekAgo = new Date();
        weekAgo?.setDate(weekAgo?.getDate() - 7);
        return reviewDate > weekAgo && r?.rating < 3;
      })?.length || 0;

      setPlatformStats({
        totalUsers,
        activeJobs,
        completedTransactions,
        totalRevenue,
        newUsersToday,
        pendingReviews,
        activeDisputes: 0, // Placeholder - no disputes table yet
        flaggedContent: 0 // Placeholder - no flags table yet
      });

      // Fetch recent users (last 10)
      const { data: recentUsersData } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, avatar_url, role, is_active, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      setRecentUsers(recentUsersData || []);

      // Fetch jobs with high urgency or many applicants (flagged for review)
      const { data: flaggedJobsData } = await supabase
        ?.from('jobs')
        ?.select(`
          id,
          title,
          category,
          status,
          urgency,
          applicant_count,
          created_at,
          user_profiles!jobs_user_id_fkey(full_name, email)
        `)
        ?.or('urgency.eq.urgent,applicant_count.gt.10')
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      setFlaggedJobs(flaggedJobsData || []);

      // Fetch low-rated reviews (flagged for oversight)
      const { data: flaggedReviewsData } = await supabase
        ?.from('ratings')
        ?.select(`
          id,
          rating,
          review,
          created_at,
          from_user:user_profiles!ratings_from_user_id_fkey(full_name, email),
          to_user:user_profiles!ratings_to_user_id_fkey(full_name, email)
        `)
        ?.lte('rating', 2)
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      setFlaggedReviews(flaggedReviewsData || []);

      // Generate analytics data (last 7 days)
      const analyticsArray = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date?.setDate(date?.getDate() - i);
        date?.setHours(0, 0, 0, 0);
        const nextDate = new Date(date);
        nextDate?.setDate(nextDate?.getDate() + 1);

        const dayUsers = usersResult?.data?.filter(u => {
          const createdDate = new Date(u?.created_at);
          return createdDate >= date && createdDate < nextDate;
        })?.length || 0;

        const dayJobs = jobsResult?.data?.filter(j => {
          const createdDate = new Date(j?.created_at);
          return createdDate >= date && createdDate < nextDate;
        })?.length || 0;

        analyticsArray?.push({
          date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          users: dayUsers,
          jobs: dayJobs,
          revenue: Math.floor(Math.random() * 50000) + 10000 // Mock revenue data
        });
      }
      setAnalyticsData(analyticsArray);

    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err?.message || 'Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      icon: 'Users',
      label: 'Total Active Users',
      value: platformStats?.totalUsers?.toLocaleString(),
      trend: 'up',
      trendValue: `+${platformStats?.newUsersToday} today`,
      color: 'primary'
    },
    {
      icon: 'Briefcase',
      label: 'Active Job Postings',
      value: platformStats?.activeJobs?.toLocaleString(),
      trend: 'up',
      trendValue: '+12%',
      color: 'accent'
    },
    {
      icon: 'CheckCircle',
      label: 'Completed Transactions',
      value: platformStats?.completedTransactions?.toLocaleString(),
      trend: 'up',
      trendValue: '+8%',
      color: 'success'
    },
    {
      icon: 'DollarSign',
      label: 'Total Revenue (PHP)',
      value: '₱' + platformStats?.totalRevenue?.toLocaleString(),
      trend: 'up',
      trendValue: '+15%',
      color: 'warning'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'jobs', label: 'Job Oversight', icon: 'Briefcase' },
    { id: 'reviews', label: 'Review Moderation', icon: 'Star' },
    { id: 'disputes', label: 'Dispute Resolution', icon: 'AlertTriangle' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-56px)] mt-[56px]">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={48} className="animate-spin text-primary" />
            <p className="text-muted-foreground">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-56px)] mt-[56px]">
          <div className="flex flex-col items-center gap-4 max-w-md text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive" />
            <h2 className="text-xl font-bold text-foreground">Error Loading Dashboard</h2>
            <p className="text-muted-foreground">{error}</p>
            <button
              onClick={fetchAdminData}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - DISKARTE Platform Management</title>
        <meta name="description" content="Comprehensive platform oversight and management for DISKARTE administrators" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        <main className="mt-[56px] px-4 md:px-6 lg:px-8 py-8 max-w-[1400px] mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Admin Dashboard
            </h1>
            <p className="text-base text-gray-600">
              Comprehensive platform oversight and management
            </p>
          </div>

          {/* Platform Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats?.map((stat) => (
              <StatsCard key={stat?.label} {...stat} />
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-8">
            <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 whitespace-nowrap transition-all duration-200 border-b-2 ${
                    activeTab === tab?.id
                      ? 'border-blue-600 text-blue-600 font-semibold bg-blue-50/50' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="text-sm md:text-base">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'overview' && (
              <>
                {/* Quick Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricsCard
                    icon="UserCheck"
                    label="New Users Today"
                    value={platformStats?.newUsersToday}
                    color="primary"
                  />
                  <MetricsCard
                    icon="AlertCircle"
                    label="Pending Reviews"
                    value={platformStats?.pendingReviews}
                    color="warning"
                  />
                  <MetricsCard
                    icon="Flag"
                    label="Flagged Content"
                    value={platformStats?.flaggedContent}
                    color="destructive"
                  />
                  <MetricsCard
                    icon="Shield"
                    label="Active Disputes"
                    value={platformStats?.activeDisputes}
                    color="accent"
                  />
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <Icon name="Users" size={20} className="text-blue-600" />
                      Recent User Registrations
                    </h3>
                    <div className="space-y-4">
                      {recentUsers?.slice(0, 5)?.map((user) => (
                        <div key={user?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <img
                              src={user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e6008c7e-1768740999976.png'}
                              alt={`${user?.full_name} avatar`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{user?.full_name}</p>
                              <p className="text-xs text-gray-600">{user?.email}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {user?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <Icon name="AlertTriangle" size={20} className="text-orange-600" />
                      Flagged Jobs (High Priority)
                    </h3>
                    <div className="space-y-4">
                      {flaggedJobs?.slice(0, 5)?.map((job) => (
                        <div key={job?.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-medium text-gray-900 text-sm">{job?.title}</p>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              job?.urgency === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {job?.urgency}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            {job?.applicant_count} applicants • Posted by {job?.user_profiles?.full_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'users' && (
              <UserManagementTable users={recentUsers} onRefresh={fetchAdminData} />
            )}

            {activeTab === 'jobs' && (
              <JobOversightTable jobs={flaggedJobs} onRefresh={fetchAdminData} />
            )}

            {activeTab === 'reviews' && (
              <ReviewOversightTable reviews={flaggedReviews} onRefresh={fetchAdminData} />
            )}

            {activeTab === 'disputes' && (
              <DisputeResolutionPanel onRefresh={fetchAdminData} />
            )}

            {activeTab === 'analytics' && (
              <AnalyticsChart data={analyticsData} />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;